import { useEffect, useState } from "react";

// LocalStorage key for the reader-history array. Structure:
//   [{ id: "abc123", ts: "2026-07-01T12:34:56.000Z" }, ...]
// - `id` = article ObjectId (24 chars). Multiple reads of the same article
//   in the same day are deduped by (id, day) so the streak reflects
//   *unique stories read*, not raw pageviews.
// - We prune entries older than 30 days on every write so localStorage
//   never grows unboundedly.
const STORAGE_KEY = "reader_history";
const HORIZON_DAYS = 30;

const _readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
};

const _writeAll = (arr) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (_) { /* ignore quota / private-browsing */ }
};

const _prune = (arr) => {
  const cutoff = Date.now() - HORIZON_DAYS * 24 * 60 * 60 * 1000;
  return arr.filter((e) => {
    const t = e && e.ts ? Date.parse(e.ts) : 0;
    return t >= cutoff;
  });
};

// Record a read. Idempotent per (article_id, day) so refreshing an
// article page doesn't inflate the streak.
export const recordArticleRead = (articleId) => {
  if (!articleId) return;
  const now = new Date();
  const day = now.toISOString().slice(0, 10);
  const all = _prune(_readAll());
  const alreadyToday = all.some(
    (e) => e.id === articleId && (e.ts || "").slice(0, 10) === day
  );
  if (alreadyToday) return;
  all.push({ id: articleId, ts: now.toISOString() });
  _writeAll(all);
};

// Count unique articles read in the last `days` days.
export const countReadsInWindow = (days = 7) => {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const seen = new Set();
  for (const e of _readAll()) {
    const t = e && e.ts ? Date.parse(e.ts) : 0;
    if (t >= cutoff && e.id) seen.add(e.id);
  }
  return seen.size;
};

// Return the most-recent unique reads in a window, optionally excluding
// today's reads (so "Continue reading" doesn't show something the visitor
// just closed). Newest-first, deduped by id.
export const getRecentReads = ({
  maxAgeDays = 7,
  excludeToday = true,
  limit = 3,
} = {}) => {
  const now = Date.now();
  const oldest = now - maxAgeDays * 24 * 60 * 60 * 1000;
  const todayKey = new Date().toISOString().slice(0, 10);
  // Sort newest-first, then dedupe by id
  const sorted = _readAll()
    .filter((e) => e && e.id && e.ts && Date.parse(e.ts) >= oldest)
    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
  const seen = new Set();
  const out = [];
  for (const e of sorted) {
    if (excludeToday && (e.ts || "").slice(0, 10) === todayKey) continue;
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push({ id: e.id, ts: e.ts });
    if (out.length >= limit) break;
  }
  return out;
};

// Reactive hook so the streak line updates as soon as a new read is
// recorded (useful when the same page mounts recordArticleRead + reads
// the count on first paint).
export const useReadingStreak = (days = 7) => {
  const [count, setCount] = useState(() => countReadsInWindow(days));
  useEffect(() => {
    setCount(countReadsInWindow(days));
    // Storage events fire when *other* tabs update localStorage; useful
    // if the visitor opens multiple articles in parallel tabs.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setCount(countReadsInWindow(days));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [days]);
  return count;
};
